import clsx from 'clsx';
import React, { useCallback, useRef, useState } from 'react';
import { Button } from '../../Button';
import { CloseIcon } from '../../Icons/CloseIcon';
import { MagnifyingGlass } from '../../Icons/MagnifyingGlass';
import './search-input.css';

interface SearchInputProps {
  baseUri: string;
}

export function SearchInput({ baseUri }: SearchInputProps) {
  const inputRef = useRef<HTMLInputElement>(null);
  const [query, setQuery] = useState<string>('');

  const onClear = useCallback(() => {
    if (!inputRef?.current) {
      return;
    }
    setQuery('');
    inputRef.current.focus();
  }, [inputRef, setQuery]);

  const onClearEnterKeydown = useCallback(
    (e: React.KeyboardEvent) => {
      if (e.key !== 'Enter' && e.key !== 'Space') {
        return;
      }
      void onClear();
    },
    [onClear],
  );

  const onSearch = useCallback(async () => {
    if (!inputRef?.current) {
      return;
    }
    const url = `${baseUri}/search/results?q=${encodeURIComponent(query)}`;
    window.location.href = url;
  }, [inputRef, query]);

  const handleEnterKeydown = useCallback(
    (e: React.KeyboardEvent) => {
      if (e.key !== 'Enter') {
        return;
      }
      void onSearch();
    },
    [onSearch],
  );

  return (
    <search className={clsx('items-center', 'flex', 'w-full', 'gap-4', 'flex-col', 'sm:flex-row')}>
      <div className={clsx('relative', 'w-full')}>
        <span>
          <MagnifyingGlass
            className={clsx('absolute', 'ml-4', 'top-1', 'w-[18px]', 'z-[1]', 'translate-y-2/4')}
          />
        </span>
        <input
          className={clsx(
            'hide-search-clear',
            'outline-none',
            'pl-[calc(2rem+18px)]',
            'w-full',
            'rounded-sm',
            'border-solid',
            'border-[1px]',
            'border-[#8b8c8f]',
            'shadow-none',
            'pr-11',
            'py-2',
            'text-medium',
            'font-normal',
            'bg-white',
            'height-[44px]',
            'transition-none',
            'focus:bg-blue-lightest',
            'focus:border-blue-light',
            'focus:shadow-[0_0_0_1px_#4b99e6]',
          )}
          type="search"
          name="satellite-header-search-input"
          ref={inputRef}
          placeholder={'Try "Best savings account?"'}
          aria-label="Search the NerdWallet site"
          value={query}
          onKeyDown={handleEnterKeydown}
          autoFocus
          onChange={e => setQuery(e.target.value)}
        />
        {query.length > 0 && (
          <span
            className={clsx(
              'cursor-pointer',
              'w-[18px]',
              'h-[18px]',
              'right-2',
              'top-1/4',
              'absolute',
            )}
            onClick={onClear}
            onKeyDown={onClearEnterKeydown}
            role="button"
            tabIndex={0}
            aria-label="Clear search input"
          >
            <CloseIcon className={clsx('fill-neutral-darker')} width="18px" height="18px" />
          </span>
        )}
      </div>
      <Button onClick={onSearch}>Search</Button>
    </search>
  );
}
